import type { ClassDefinition } from '@/schemas/classDefinition';
import type { StandardRace } from '@/schemas/race';

/**
 * **FI-83 (2026-05-24)** — Pure helper SSOT per l'auto-select dell'archetipo
 * in funzione della razza.
 *
 * Calcola il prossimo `archetypeId` da settare (se necessario) data la
 * transizione `prevRaceId` → `currentRaceId` e l'`currentArchetypeId`
 * corrente del draft. Usato dal `useEffect` di `<SectionArchetype>`.
 *
 * **Regole**:
 *  1. **Auto-preselect iniziale**: se non c'è archetypeId e la razza
 *     corrente ha un archetipo tipico (`associatedRace === currentRaceId`),
 *     suggerisce quello.
 *  2. **Auto-bump al cambio razza**: se la razza è cambiata
 *     (`prevRaceId !== currentRaceId`) e l'archetipo corrente era il
 *     tipico della razza vecchia, propone il tipico della razza nuova
 *     (preserva le scelte manuali divergenti dal default).
 *
 * Ritorna `undefined` quando nessun dispatch è necessario (no-op):
 *  - L'utente ha una scelta manuale che non era il default della razza vecchia.
 *  - La razza non ha un archetipo tipico (es. Barbaro, custom race).
 *  - Il computed coincide già con l'archetypeId corrente.
 *
 * **Caveat noto** (trade-off documentato): non distingue scelta "auto" da
 * scelta "manuale che coincide col default". Se l'utente avesse scelto
 * manualmente Halfling-archetipo per razza Halfling e poi cambiasse
 * razza a Nano, la sua scelta verrebbe sostituita dal default Nano. Caso
 * edge poco realistico (chi sceglie esplicitamente il default è
 * indistinguibile da chi accetta il default).
 */
export function computeArchetypeAutoSelect(
  classDef: ClassDefinition,
  prevRaceId: StandardRace | undefined,
  currentRaceId: StandardRace | undefined,
  currentArchetypeId: string | undefined,
): string | undefined {
  const suggestedForCurrentRace = findSuggestedArchetypeId(classDef, currentRaceId);

  // Caso 1: auto-preselect iniziale (no archetype yet).
  if (currentArchetypeId === undefined && suggestedForCurrentRace !== undefined) {
    return suggestedForCurrentRace;
  }

  // Caso 2: cambio razza, e archetipo corrente era il suggested della
  // razza vecchia → bump al suggested della razza nuova.
  if (
    prevRaceId !== undefined &&
    prevRaceId !== currentRaceId &&
    currentArchetypeId !== undefined &&
    suggestedForCurrentRace !== undefined &&
    suggestedForCurrentRace !== currentArchetypeId
  ) {
    const suggestedForPrevRace = findSuggestedArchetypeId(classDef, prevRaceId);
    if (currentArchetypeId === suggestedForPrevRace) {
      return suggestedForCurrentRace;
    }
  }

  return undefined;
}

function findSuggestedArchetypeId(
  classDef: ClassDefinition,
  raceId: StandardRace | undefined,
): string | undefined {
  if (raceId === undefined) return undefined;
  return classDef.archetypes.find((a) => a.associatedRace === raceId)?.id;
}
